/**
 * Rocket physical parameters with validation.
 *
 * Mass is derived as dryMass + fuel; fuel is the only independently tracked
 * quantity. Mass is never integrated separately.
 */

import type { RocketParams, SimulationConfig } from "./types";
import { isFiniteState } from "./types";

export class RocketParamsValidator {
  private constructor() {} // prevent instantiation

  static validate(params: RocketParams): void {
    if (!Number.isFinite(params.dryMass) || params.dryMass <= 0) {
      throw new Error(`RocketParams.dryMass: must be a positive finite number (got ${params.dryMass})`);
    }
    if (!Number.isFinite(params.maxFuel) || params.maxFuel < 0) {
      throw new Error(`RocketParams.maxFuel: must be a non-negative finite number (got ${params.maxFuel})`);
    }
    if (!Number.isFinite(params.maxThrust) || params.maxThrust <= 0) {
      throw new Error(`RocketParams.maxThrust: must be a positive finite number (got ${params.maxThrust})`);
    }
    if (!Number.isFinite(params.fuelFlowRate) || params.fuelFlowRate < 0) {
      throw new Error(`RocketParams.fuelFlowRate: must be a non-negative finite number (got ${params.fuelFlowRate})`);
    }
    if (!Number.isFinite(params.dragCoefficient) || params.dragCoefficient < 0) {
      throw new Error(`RocketParams.dragCoefficient: must be a non-negative finite number (got ${params.dragCoefficient})`);
    }
    if (!Number.isFinite(params.referenceArea) || params.referenceArea < 0) {
      throw new Error(`RocketParams.referenceArea: must be a non-negative finite number (got ${params.referenceArea})`);
    }
    if (!Number.isFinite(params.inertiaY) || params.inertiaY <= 0) {
      throw new Error(`RocketParams.inertiaY: must be a positive finite number (got ${params.inertiaY})`);
    }
    if (!Number.isFinite(params.centerOfMassOffset) || params.centerOfMassOffset < 0) {
      throw new Error(`RocketParams.centerOfMassOffset: must be a non-negative finite number (got ${params.centerOfMassOffset})`);
    }
  }

  /** Total mass = dry mass + remaining fuel (kg). */
  static computeMass(dryMass: number, fuel: number): number {
    return dryMass + fuel;
  }

  /** Total mass at launch = dry mass + max fuel (kg). */
  static computeLaunchMass(dryMass: number, maxFuel: number): number {
    return dryMass + maxFuel;
  }
}

export function createRocketParams(params: RocketParams): RocketParams {
  RocketParamsValidator.validate(params);
  return { ...params };
}

/**
 * Validates a complete SimulationConfig, including environment, rocket,
 * initial state, and simulation-level parameters.
 */
export function validateSimulationConfig(config: SimulationConfig): void {
  // Environment
  if (!Number.isFinite(config.environment.gravity) || config.environment.gravity <= 0) {
    throw new Error("SimulationConfig.environment.gravity: must be a positive finite number");
  }
  if (!Number.isFinite(config.environment.seaLevelDensity) || config.environment.seaLevelDensity < 0) {
    throw new Error("SimulationConfig.environment.seaLevelDensity: must be a non-negative finite number");
  }
  if (
    config.environment.scaleHeight !== Infinity &&
    (!Number.isFinite(config.environment.scaleHeight) || config.environment.scaleHeight <= 0)
  ) {
    throw new Error("SimulationConfig.environment.scaleHeight: must be a positive finite number or Infinity");
  }

  // Rocket
  RocketParamsValidator.validate(config.rocket);

  // Initial state
  if (!isFiniteState(config.initialState)) {
    throw new Error("SimulationConfig.initialState: contains non-finite values");
  }
  if (
    !Number.isFinite(config.initialState.fuel) ||
    config.initialState.fuel < 0 ||
    config.initialState.fuel > config.rocket.maxFuel
  ) {
    throw new Error(
      `SimulationConfig.initialState.fuel: must be between 0 and maxFuel (got ${config.initialState.fuel})`
    );
  }

  // Simulation parameters
  if (!Number.isFinite(config.maxGimbalAngle) || config.maxGimbalAngle <= 0) {
    throw new Error("SimulationConfig.maxGimbalAngle: must be a positive finite number");
  }
  if (!Number.isFinite(config.physicsDt) || config.physicsDt <= 0) {
    throw new Error("SimulationConfig.physicsDt: must be a positive finite number");
  }
  if (!Number.isFinite(config.outputDt) || config.outputDt <= 0) {
    throw new Error("SimulationConfig.outputDt: must be a positive finite number");
  }
  const ratio = config.outputDt / config.physicsDt;
  if (!Number.isFinite(ratio) || Math.abs(Math.round(ratio) - ratio) > 1e-9) {
    throw new Error(
      `SimulationConfig.outputDt must be an integer multiple of physicsDt (ratio = ${ratio})`
    );
  }
  if (!Number.isFinite(config.maxDuration) || config.maxDuration <= 0) {
    throw new Error("SimulationConfig.maxDuration: must be a positive finite number");
  }
  if (!Number.isFinite(config.landingTargetX)) {
    throw new Error("SimulationConfig.landingTargetX: must be a finite number");
  }
  if (!Number.isFinite(config.maxTouchdownVerticalSpeed) || config.maxTouchdownVerticalSpeed < 0) {
    throw new Error("SimulationConfig.maxTouchdownVerticalSpeed: must be a non-negative finite number");
  }
  if (!Number.isFinite(config.maxTouchdownHorizontalSpeed) || config.maxTouchdownHorizontalSpeed < 0) {
    throw new Error("SimulationConfig.maxTouchdownHorizontalSpeed: must be a non-negative finite number");
  }
  if (!Number.isFinite(config.maxHorizontalError) || config.maxHorizontalError < 0) {
    throw new Error("SimulationConfig.maxHorizontalError: must be a non-negative finite number");
  }
  if (!Number.isFinite(config.maxTouchdownAttitude) || config.maxTouchdownAttitude < 0) {
    throw new Error("SimulationConfig.maxTouchdownAttitude: must be a non-negative finite number");
  }
  if (!Number.isFinite(config.maxTouchdownAngularRate) || config.maxTouchdownAngularRate < 0) {
    throw new Error("SimulationConfig.maxTouchdownAngularRate: must be a non-negative finite number");
  }
  if (!Number.isFinite(config.descentRate) || config.descentRate < 0) {
    throw new Error("SimulationConfig.descentRate: must be a non-negative finite number");
  }
  if (!Number.isFinite(config.flareAltitude) || config.flareAltitude < 0) {
    throw new Error("SimulationConfig.flareAltitude: must be a non-negative finite number");
  }
}
